import { Close } from "@mui/icons-material";
import { Box, BoxProps, IconButton, styled } from "@mui/material";

export type CoordinateLabelDivProps = {
    id: string;
    formattedPos: [string, string, string];
    fontSize?: number;
    labelSVGHeight?: number;
    fillColor?: string;
    fillColorBox?: string;
    canvas?: HTMLCanvasElement | null;
    onRemove?: () => void;
    /** When true, the remove button is always visible (no hover required). */
    alwaysShowRemove?: boolean;
    onPointerEnter?: () => void;
    onPointerLeave?: () => void;
    onClick?: () => void;
};

/**
 * Utility to build the coordinate label as a React div+svg element.
 * Used by CoordinateLabel and overlays.
 */
export function CoordinateLabelHUD({
    id,
    formattedPos,
    fontSize = 12,
    labelSVGHeight = 16,
    fillColor = "#FFFFFF",
    fillColorBox = "#000000ff",
    canvas,
    onRemove,
    alwaysShowRemove,
    onPointerEnter,
    onPointerLeave,
    onClick,
}: CoordinateLabelDivProps) {
    const labelText = (
        <Box
            className="label-label-text"
            sx={{
                borderRadius: "4px",
                paddingRight: "4px",
                paddingLeft: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: fillColorBox,
                color: fillColor,
                fontWeight: 600,
                height: labelSVGHeight,
                fontSize: `${fontSize}px`,
                whiteSpace: "nowrap",
                textAnchor: "middle",
                textAlign: "center",
                fontFamily: "Inter, sans-serif",
            }}
        >
            <span style={{ fontWeight: 600, marginLeft: 6, marginRight: 6 }}>X:</span>
            <span style={{ fontWeight: 500 }}>{formattedPos[0]}</span>
            <span style={{ fontWeight: 600, marginLeft: 4, marginRight: 6 }}>Y:</span>
            <span style={{ fontWeight: 500 }}>{formattedPos[1]}</span>
            <span style={{ fontWeight: 600, marginLeft: 4, marginRight: 6 }}>Z:</span>
            <span style={{ fontWeight: 500, marginRight: 6 }}>{formattedPos[2]}</span>
        </Box>
    );

    if (alwaysShowRemove && onRemove) {
        return (
            <Container id={id} labelSVGHeight={labelSVGHeight} className="coordinate-label-hovered" onClick={onClick}>
                {labelText}
                <IconButton
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        background: "#F3BEBE",
                        color: "#C42223",
                        marginLeft: "6px",
                        "&:hover": { background: "#F3BEBE" },
                    }}
                    onClick={onRemove}
                >
                    <Close sx={{ fontSize: 20 }} />
                </IconButton>
            </Container>
        );
    }

    return (
        <Container
            id={id}
            labelSVGHeight={labelSVGHeight}
            onClick={onClick}
            onPointerEnter={(e) => {
                if (e.currentTarget) {
                    e.currentTarget.classList.toggle("coordinate-label-hovered", true);
                }
                if (canvas) {
                    canvas.style.cursor = "default";
                }
                onPointerEnter?.();
            }}
            onPointerLeave={(e) => {
                if (e.currentTarget) {
                    (e.currentTarget as HTMLElement).classList.toggle("coordinate-label-hovered", false);
                }
                onPointerLeave?.();
            }}
        >
            {onRemove && (
                <Box
                    className="coordinate-label-btn-container"
                    sx={{
                        position: "absolute",
                        left: "100%",
                        height: "100%",
                        backgroundColor: fillColorBox,
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        marginLeft: "-4px",
                        padding: "1px 2px",
                        paddingLeft: "5px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                    }}
                >
                    <IconButton
                        sx={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            background: "#F3BEBE",
                            color: "#C42223",
                            "&:hover": {
                                background: "#F3BEBE",
                            },
                        }}
                        onClick={onRemove}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            )}
            {labelText}
        </Container>
    );
}

const Container = styled(Box, { shouldForwardProp: (prop) => prop !== "labelSVGHeight" })<
    BoxProps & { labelSVGHeight: number }
>(({ labelSVGHeight }) => ({
    position: "absolute",
    transform: "translate(-50%, 0)",
    transformOrigin: "0 0",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&.coordinate-label-hovered": {
        zIndex: "9999 !important",
    },
    "&:not(.coordinate-label-hovered) .coordinate-label-btn-container": {
        width: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
    "&.coordinate-label-hovered .label-label-text": {
        padding: "2px 4px",
        marginRight: "-4px",
        height: labelSVGHeight + 4,
    },
}));
